import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Send, ArrowLeft, User, Shield, Clock, Package, CheckCircle } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';
import { formatPrice } from '../../utils/currency';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminSupportTicketDetail = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const messagesEndRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [ticketData, setTicketData] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    loadTicketData();
    
    const interval = setInterval(() => {
      loadTicketData(true);
    }, 10000);
    
    return () => clearInterval(interval);
  }, [ticketId]);

  useEffect(() => {
    scrollToBottom();
  }, [ticketData?.messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadTicketData = async (silent = false) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API}/support/admin/tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTicketData(response.data);
    } catch (error) {
      console.error('Error loading ticket:', error);
      if (!silent) {
        toast({
          title: 'Error',
          description: 'Failed to load ticket details',
          variant: 'destructive'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      const token = localStorage.getItem('accessToken');
      await axios.post(
        `${API}/support/admin/tickets/${ticketId}/messages`,
        { ticketId, message: newMessage, attachments: [] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setNewMessage('');
      await loadTicketData(true);
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: 'Error',
        description: 'Failed to send message',
        variant: 'destructive'
      });
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      const token = localStorage.getItem('accessToken');
      await axios.patch(
        `${API}/support/admin/tickets/${ticketId}/status`,
        { status: newStatus, notes: `Status updated to ${newStatus}` },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({
        title: 'Status Updated',
        description: `Ticket status changed to ${newStatus}`
      });
      
      await loadTicketData(true);
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update ticket status',
        variant: 'destructive'
      });
    } finally {
      setUpdatingStatus(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'text-blue-600 bg-blue-50',
      in_progress: 'text-yellow-600 bg-yellow-50',
      resolved: 'text-green-600 bg-green-50',
      closed: 'text-gray-600 bg-gray-50'
    };
    return colors[status] || colors.open;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading ticket details...</div>
      </div>
    );
  }

  if (!ticketData) {
    return (
      <div className="p-6">
        <div className="text-center text-gray-500">Ticket not found</div>
      </div>
    );
  }

  const { ticket, order, user, messages, orderStatusHistory } = ticketData;

  return (
    <div className="p-6">
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate('/admin/support')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Tickets
        </Button>
        
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{ticket.subject}</h1>
            <p className="text-sm text-gray-500 mt-1">Ticket ID: {ticket.id}</p>
          </div>
          
          <Select 
            value={ticket.status} 
            onValueChange={handleStatusChange}
            disabled={updatingStatus}
          >
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <h2 className="font-semibold text-gray-900">Conversation</h2>
            </div>
            
            <div className="p-4 h-96 overflow-y-auto bg-gray-50">
              {messages && messages.length > 0 ? (
                messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`mb-4 flex ${msg.senderType === 'admin' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-md rounded-lg p-3 ${
                        msg.senderType === 'admin'
                          ? 'bg-blue-600 text-white'
                          : msg.senderType === 'system'
                          ? 'bg-gray-200 text-gray-700 text-center mx-auto'
                          : 'bg-white text-gray-900 border border-gray-200'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        {msg.senderType === 'admin' && <Shield className="w-4 h-4" />}
                        {msg.senderType === 'customer' && <User className="w-4 h-4" />}
                        <span className="text-xs font-medium">{msg.senderName}</span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                      <p className={`text-xs mt-2 ${
                        msg.senderType === 'admin' ? 'text-blue-100' : 'text-gray-500'
                      }`}>
                        {formatDate(msg.createdAt)}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center text-gray-500">No messages yet</div>
              )}
              <div ref={messagesEndRef} />
            </div>
            
            <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-200 bg-white">
              <div className="flex gap-2">
                <Textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type your response..."
                  className="flex-1 min-h-20"
                  disabled={sending}
                />
                <Button type="submit" disabled={sending || !newMessage.trim()}>
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </form>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-900 mb-3">Ticket Details</h3>
            <dl className="space-y-2 text-sm">
              <div>
                <dt className="text-gray-500">Priority</dt>
                <dd className="font-medium text-gray-900 capitalize">{ticket.priority}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Created</dt>
                <dd className="font-medium text-gray-900">{formatDate(ticket.createdAt)}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Last Updated</dt>
                <dd className="font-medium text-gray-900">{formatDate(ticket.updatedAt)}</dd>
              </div>
              {ticket.resolvedAt && (
                <div>
                  <dt className="text-gray-500">Resolved</dt>
                  <dd className="font-medium text-gray-900">{formatDate(ticket.resolvedAt)}</dd>
                </div>
              )}
            </dl>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-900 mb-3">Customer Info</h3>
            <dl className="space-y-2 text-sm">
              <div>
                <dt className="text-gray-500">Name</dt>
                <dd className="font-medium text-gray-900">{user?.name || 'N/A'}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Email</dt>
                <dd className="font-medium text-gray-900">{user?.email}</dd>
              </div>
            </dl>
          </div>

          {order && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Package className="w-4 h-4" />
                Order Details
              </h3>
              <dl className="space-y-2 text-sm">
                <div>
                  <dt className="text-gray-500">Order Number</dt>
                  <dd className="font-medium text-gray-900">{order.orderNumber}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Status</dt>
                  <dd className="font-medium text-gray-900 capitalize">{order.status}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Total</dt>
                  <dd className="font-medium text-gray-900">{formatPrice(order.total, order.currency)}</dd>
                </div>
              </dl>
            </div>
          )}

          {orderStatusHistory && orderStatusHistory.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Order History
              </h3>
              <div className="space-y-3">
                {orderStatusHistory.map((history) => (
                  <div key={history.id} className="text-sm">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-3 h-3 text-green-500" />
                      <span className="font-medium capitalize">{history.status}</span>
                    </div>
                    <p className="text-xs text-gray-500 ml-5">
                      {formatDate(history.timestamp)}
                    </p>
                    {history.notes && (
                      <p className="text-xs text-gray-600 ml-5 mt-1">{history.notes}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSupportTicketDetail;
